export const problemCards = [
  {
    variant: 'blue' as const,
    title: 'What if your essential pediatric OT resources were already organized?',
    paragraphs: [
      'Every child has different needs, abilities, interests, and participation goals. One struggles with everyday materials, another with handwriting, another with bilateral coordination or self-care.',
      'And each intervention requires more than a general activity idea. You need:',
    ],
    list: [
      'A clear functional objective.',
      'Appropriate materials and a practical setup.',
      'Step-by-step instructions.',
      'Meaningful adaptations and alternatives.',
      'Observable performance indicators.',
    ],
    outro: [
      'Pediatric OT Complete Therapy System™ brings six essential intervention areas together with five complementary professional resources.',
    ],
    highlight: 'Assess. Select. Implement. Adapt. Review.',
  },
  {
    variant: 'gold' as const,
    title: 'Spend less time rebuilding activities from scratch',
    paragraphs: [
      "Your attention belongs on understanding the child's needs and supporting meaningful participation — not on repeatedly searching for activities, writing instructions, or assembling separate resources.",
    ],
    outro: [
      "Open the relevant volume, locate a functional activity, review the visual steps, and adapt the intervention to your client's goals.",
    ],
    highlight: 'More organized resources. More complete intervention options.',
  },
  {
    variant: 'navy' as const,
    title: 'Practical tools for different pediatric OT challenges',
    paragraphs: [],
    qa: [
      { q: 'Fine motor difficulties?', a: 'Functional activities for hand use, precision, manipulation, and everyday materials.' },
      { q: 'Handwriting challenges?', a: 'Pre-writing, letter formation, spacing, alignment, and functional writing.' },
      { q: 'Bilateral coordination difficulties?', a: 'Coordinated hand use, movement sequences, and motor planning.' },
      { q: 'Sensory-related participation barriers?', a: 'Environmental adaptations, communication supports, and meaningful choices.' },
      { q: 'Daily living skills?', a: 'Dressing, hygiene, mealtime participation, and self-care routines.' },
      { q: 'School participation difficulties?', a: 'Task initiation, classroom organization, instructions, and assignments.' },
    ],
    highlight: 'Open the resource. Find the activity. Adapt it to your client.',
  },
];

export const volumes = [
  {
    number: '01',
    title: 'Fine Motor Skills & Hand Function',
    subtitle: 'Practical Interventions for Functional Hand Skills',
    points: ['Visual activities built around purposeful hand use and manipulation during meaningful tasks.'],
    list: [
      'Object grasping and manipulation',
      'Controlled hand movements',
      'Precision object placement',
      'Everyday materials and tools',
      'Stabilization and positioning',
      'Hand coordination in practical tasks',
    ],
    tagline: 'Functional participation — not disconnected finger exercises.',
    finalLine: 'Prepare. Manipulate. Coordinate. Apply.',
    meta: '40 Pages of Professional Interventions',
  },
  {
    number: '02',
    title: 'Pre-Writing & Handwriting Skills',
    subtitle: 'Functional Handwriting Interventions for Pediatric OT',
    points: ['Visually structured interventions connected to real communication and school tasks.'],
    list: [
      'Functional pre-writing marks',
      'Letter production, orientation and sizing',
      'Baseline alignment and word spacing',
      'Near-point and far-point copying',
      'Adapted paper and writing tools',
      'Handwriting readability',
    ],
    finalLine: 'Observe. Write. Adapt. Review.',
    meta: '40 Pages of Professional Interventions',
  },
  {
    number: '03',
    title: 'Bilateral Coordination & Motor Planning',
    subtitle: 'Visual Activities for Coordinated Functional Movement',
    points: ['Clear visual sequences, material requirements, and modifications for coordinated hand use and movement organization.'],
    list: [
      'Bilateral hand coordination',
      'Stabilization and manipulation',
      'Functional construction activities',
      'Movement planning during play',
      'Partner and cooperative tasks',
      'Adapting to changing task demands',
    ],
    tagline: 'Meaningful motor participation — not repeated movement drills.',
    finalLine: 'Plan. Coordinate. Perform. Adapt.',
    meta: '40 Pages of Professional Interventions',
  },
  {
    number: '04',
    title: 'Sensory-Informed Regulation & Participation',
    subtitle: 'Individualized Strategies for Functional Participation',
    points: ['Centered on the interaction between the child, the activity, and the environment — emphasizing comfort, autonomy, and accommodations.'],
    list: [
      'Communicating needs and requesting breaks',
      'Noise, lighting and visual adjustments',
      'Tactile material choices',
      'Preparing for transitions',
      'Flexible group participation',
      'Reviewing selected supports',
    ],
    finalLine: 'Identify. Adapt. Participate. Review.',
    meta: '40 Pages of Professional Interventions',
  },
  {
    number: '05',
    title: 'Daily Living & Self-Care Independence',
    subtitle: 'Functional Pediatric Activities of Daily Living',
    points: ['Practical routines, real materials, clear steps, and assistance options for everyday self-care.'],
    list: [
      'Dressing, buttons, zippers and shoes',
      'Handwashing and oral care',
      'Hair grooming and hygiene',
      'Functional utensil use',
      'Mealtime prep and cleanup',
      'Belongings and home routines',
    ],
    finalLine: 'Prepare. Practice. Adapt. Participate.',
    meta: '40 Pages of Professional Interventions',
  },
  {
    number: '06',
    title: 'School Participation & Executive Skills',
    subtitle: 'Practical Classroom Participation Interventions',
    points: ['Executive skills supported through meaningful educational occupations during real classroom activities.'],
    list: [
      'Preparing functional workspaces',
      'Starting classroom assignments',
      'Following multistep instructions',
      'Organizing folders and supplies',
      'Requesting appropriate help',
      'Completing and submitting work',
    ],
    tagline: 'Not generic attention exercises or abstract worksheets.',
    finalLine: 'Prepare. Begin. Organize. Complete.',
    meta: '40 Pages of Professional Interventions',
  },
];

// Pages shown in the "Take a look inside" carousel, in order 1–6.
export const insidePages = [
  {
    image: 'https://cdn.magicpatterns.com/uploads/atdKDPMLMotSoJLbvHe516/Captura_de_pantalla_2026-11-03_163129.png',
    alt: 'FM-14 Object Orientation Matching — visual steps, materials, dose, and easier/advanced adaptations.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/6Nd2qUV12aR2gU4Z9mieXv/Captura_de_pantalla_2026-11-03_163017.png',
    alt: 'FM-03 Clothespin Card Clipping — functional pinch during controlled placement.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/2xz1nciwX7QLU4fMqD1udp/Captura_de_pantalla_2026-11-03_163038.png',
    alt: 'FM-07 Peel-and-Place Label Activity — precision pinch and release with adhesive stickers.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/4ketwm7SSvUwrojnM23mPp/Captura_de_pantalla_2026-11-03_163049.png',
    alt: 'FM-08 Thick Card Slot Insertion — object orientation and precision insertion.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/1sMmeaZxTU2b178wV83Tzj/Captura_de_pantalla_2026-11-03_163102.png',
    alt: 'FM-09 Mixed Game Piece Sorting — functional manipulation of differently shaped objects.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/mmKjWgrufMTijdAqFC2TL3/Captura_de_pantalla_2026-11-03_163117.png',
    alt: 'FM-12 Controlled Object Rotation — rotating an object within the hand for functional orientation.',
  },
];

export const bonuses = [
  {
    number: '01',
    title: '40 Complete Ready-to-Use OT Sessions',
    subtitle: 'Complete Pediatric Occupational Therapy Session Plans',
    price: 14.9,
    intro: ['40 organized sessions built around meaningful functional goals — each one planned as a complete session, not a list of unrelated activities.'],
    list: [
      'Functional session objectives',
      'Materials and environment preparation',
      'Step-by-step session structure',
      'Easier and more advanced options',
      'Observable functional outcomes',
    ],
    finalLine: 'Select. Prepare. Implement. Observe.',
    meta: '40 Complete Sessions | 40 Pages',
  },
  {
    number: '02',
    title: 'Pediatric OT Assessment & Progress Tracking Toolkit',
    subtitle: '40 Functional Observation and Documentation Tools',
    price: 12.9,
    intro: ['Organize observations, goals, assistance levels, and progress:'],
    list: [
      'Family and school interview templates',
      'Hand-use, handwriting and self-care records',
      'Goal-setting and baseline sheets',
      'Functional progress graphs',
      'Teacher and family communication forms',
    ],
    outro: ['Original, nonstandardized tools that complement — not replace — validated assessments.'],
    finalLine: 'Observe. Document. Compare. Review.',
    meta: '40 Professional Tools | 40 Pages',
  },
  {
    number: '03',
    title: 'Home & Classroom Carryover Programs',
    subtitle: '40 Programs · 120 Practical Activity Opportunities',
    price: 11.9,
    intro: ['Extend individualized goals into daily life with resources for caregivers and teachers:'],
    list: [
      'Home play and family projects',
      'Classroom participation opportunities',
      'Household contributions',
      'Community participation',
      'Using skills across settings',
    ],
    finalLine: 'Practice. Adapt. Share. Observe.',
    meta: '40 Programs | 40 Pages',
  },
  {
    number: '04',
    title: 'Pediatric OT Printable Visual Supports Library',
    subtitle: '40 Ready-to-Print Visual Support Resources',
    price: 9.9,
    intro: ['Printable tools for communication, routines, and participation:'],
    list: [
      'Choice, help-request and break cards',
      'Visual task and transition sequences',
      'Dressing, handwashing and oral-care visuals',
      'Classroom organization guides',
      'Customizable participation boards',
    ],
    finalLine: 'Print. Select. Customize. Use.',
    meta: '40 Visual Resources | 40 Pages',
  },
  {
    number: '05',
    title: 'Clinical Problem-Solving & Adaptation Maps',
    subtitle: '40 Visual Clinical Decision and Adaptation Maps',
    price: 12.9,
    intro: ['For when an activity needs modification, more support, or reassessment:'],
    list: [
      'Difficulty starting a task',
      'Excessive demands, fatigue or discomfort',
      'Handwriting and motor-sequence challenges',
      'Sensory-related participation barriers',
      'Differences across settings',
    ],
    outro: ['Designed to complement individualized clinical reasoning — not replace it.'],
    finalLine: 'Identify. Analyze. Adapt. Reassess.',
    meta: '40 Clinical Maps | 40 Pages',
  },
];

export const benefits = [
  { title: 'Complete visual interventions', text: 'Functional goals, materials, instructions, and adaptations.' },
  { title: 'Built for real occupations', text: 'Practical clinical application — not introductory theory.' },
  { title: 'Printable and reusable', text: 'Digital materials for reference and permitted printing.' },
  { title: 'One organized collection', text: 'Everything you need in one place, ready to adapt.' },
];

export const offerItems = [
  '6 professional pediatric OT volumes',
  '5 FREE complementary resource collections',
  '240 visual interventions',
  '40 complete therapy sessions',
  '40 assessment and tracking tools',
  '40 home and classroom programs',
  '40 printable visual supports',
  '40 clinical adaptation maps',
];

export const audiences = [
  { title: 'Pediatric occupational therapists', text: 'Interventions for hand skills, writing, movement, and daily activities.' },
  { title: 'School-based OTs', text: 'Classroom participation, assignments, routines, and school supports.' },
  { title: 'Pediatric outpatient therapists', text: 'Visual interventions for individualized goals in clinical settings.' },
  { title: 'Occupational therapy assistants', text: 'For use within appropriate scope and applicable supervision.' },
  { title: 'Pediatric rehabilitation teams', text: 'Complementary tools for collaborative functional participation.' },
  { title: 'Working with families & teachers', text: 'Clear communication about activities and adaptations.' },
];

export const testimonials = [
  {
    name: 'Rachel M.',
    age: 34,
    role: 'School-based occupational therapist · Columbus, USA',
    quote:
      'Most of my caseload is handwriting and classroom participation. Volume 06 gave me ready-made, visual steps for task initiation and organizing materials. I used to spend Sunday nights building worksheets — now I adapt an activity in a few minutes and focus on the kids.',
  },
  {
    name: 'Emily T.',
    age: 29,
    role: 'Pediatric outpatient OT · Leeds, UK',
    quote:
      "I'm three years into practice and the 40 session plans have been a lifesaver. Each one lists materials, setup, and easier or harder options, so I walk in with a clear plan and still individualize it. The fine motor activities with photos are my go-to for 5–7 year olds.",
  },
  {
    name: 'Laura G.',
    age: 42,
    role: 'Pediatric OT, private practice · Melbourne, Australia',
    quote:
      'The printable visual supports and home carryover programs changed how I work with families. Parents finally have clear, picture-based steps for dressing and handwashing at home, and I can see the difference in carryover between sessions.',
  },
];

export const faqs = [
  { q: 'Is this a physical product?', a: 'No. It is a 100% digital collection of professional resources. Nothing will be shipped.' },
  {
    q: 'Who is this system designed for?',
    a: 'Primarily pediatric occupational therapists in outpatient clinics, schools, pediatric rehabilitation, and related settings.',
  },
  { q: 'What language is the collection in?', a: 'The complete collection is provided in English.' },
  {
    q: 'Is this a course or certification?',
    a: 'No. It is a professional resource collection. It does not provide a license, continuing education credits, or certification.',
  },
  {
    q: 'Is it a beginner textbook?',
    a: 'No. It focuses on practical intervention resources rather than introductory theory, and is intended for professionals with appropriate training.',
  },
  {
    q: 'Which pediatric OT areas are covered?',
    a: 'Fine motor skills, handwriting, bilateral coordination, motor planning, sensory-informed participation, daily living and self-care, school participation, and executive skills.',
  },
  {
    q: 'Does it include complete sessions and printables?',
    a: 'Yes. Bonus 01 includes 40 complete session plans, and the collection includes visual supports, observation tools, and programs designed for permitted professional printing.',
  },
  {
    q: 'Can I adapt the activities for different children?',
    a: "Yes. Activities include options for modifying demands, materials, assistance, and participation methods. Suitability depends on each child's abilities and your professional assessment — not diagnosis alone.",
  },
  {
    q: 'Do the materials replace clinical judgment?',
    a: 'No. The tools are not standardized assessments and do not replace individualized assessment, clinical reasoning, medical guidance, or licensing requirements.',
  },
  {
    q: 'How will I receive my purchase?',
    a: "Once payment is completed, the materials are delivered to the email entered at checkout (check spam if needed). Support: compradigitalyaa@gmail.com.",
  },
];

export const recentBuyers = [
  { name: 'Ashley Carter', place: 'Dallas, United States', minutes: 3 },
  { name: 'Sophie Laurent', place: 'Lyon, France', minutes: 7 },
  { name: 'Valentina Gómez', place: 'Bogotá, Colombia', minutes: 5 },
  { name: 'Chloe Mitchell', place: 'Auckland, New Zealand', minutes: 12 },
  { name: 'Priya Sharma', place: 'Toronto, Canada', minutes: 4 },
  { name: 'Hannah Becker', place: 'Hamburg, Germany', minutes: 9 },
  { name: 'Olivia Bennett', place: 'Brisbane, Australia', minutes: 14 },
  { name: 'Niamh Kelly', place: 'Cork, Ireland', minutes: 6 },
  { name: 'Sara Lindqvist', place: 'Gothenburg, Sweden', minutes: 11 },
];
